/**
 * Warehouse Management Service
 *
 * Handles all warehouse movement operations including:
 * - Inbound movements (from Purchase Orders)
 * - Outbound movements (from Sales Orders or manual)
 * - Movement confirmation and cancellation
 * - Statistics and reporting
 */

import httpClient from './httpClient';

const warehouseService = {
  // ==================== List & Search ====================

  /**
   * Get list of warehouse movements with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {string} params.movement_type - 'inbound' | 'outbound' | 'transfer'
   * @param {string} params.status - 'pending' | 'completed' | 'cancelled'
   * @param {string} params.reference_type - 'purchase_order' | 'sales_order' | 'manual'
   * @param {string} params.client_ref - Client reference ID
   * @param {string} params.date_from - Start date for movement_date filter
   * @param {string} params.date_to - End date for movement_date filter
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.page_size - Items per page (default 20)
   */
  async getMovements(params = {}) {
    try {
      const response = await httpClient.get('/api/warehouse-movements', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse movements:', error);
      throw error;
    }
  },

  /**
   * Get a single warehouse movement by ID
   * @param {string} movementId - Movement ID
   */
  async getMovement(movementId) {
    try {
      const response = await httpClient.get(`/api/warehouse-movements/${movementId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse movement:', error);
      throw error;
    }
  },

  // ==================== Create Operations ====================

  /**
   * Create a new warehouse movement (typically outbound)
   * @param {Object} movementData - Movement data
   */
  async createMovement(movementData) {
    try {
      const response = await httpClient.post('/api/warehouse-movements', movementData);
      return response.data;
    } catch (error) {
      console.error('Error creating warehouse movement:', error);
      throw error;
    }
  },

  /**
   * Create outbound movement from a sales order
   * @param {Object} data - Request data
   * @param {string} data.sales_order_id - Sales order ID
   * @param {Array} data.selected_products - Optional array of product IDs
   * @param {string} data.location - Warehouse location
   * @param {string} data.delivery_address - Delivery address
   * @param {string} data.notes - Additional notes
   */
  async createOutboundFromSalesOrder(data) {
    try {
      const response = await httpClient.post('/api/warehouse-movements/from-sales-order', data);
      return response.data;
    } catch (error) {
      console.error('Error creating outbound from sales order:', error);
      throw error;
    }
  },

  /**
   * Create inbound movement from a purchase order
   * @param {Object} data - Request data
   * @param {string} data.purchase_order_id - Purchase order ID
   * @param {Array} data.selected_products - Optional array of product IDs
   * @param {string} data.location - Warehouse location
   * @param {string} data.notes - Additional notes
   */
  async createInboundFromPurchaseOrder(data) {
    try {
      const response = await httpClient.post('/api/warehouse-movements/from-purchase-order', data);
      return response.data;
    } catch (error) {
      console.error('Error creating inbound from purchase order:', error);
      throw error;
    }
  },

  /**
   * Create multiple outbound movements in batch
   * @param {Array} movements - Array of movement data objects
   */
  async createBatchOutbound(movements) {
    try {
      const response = await httpClient.post('/api/warehouse-movements/batch-outbound', { movements });
      return response.data;
    } catch (error) {
      console.error('Error creating batch outbound:', error);
      throw error;
    }
  },

  // ==================== Update Operations ====================

  /**
   * Update a warehouse movement (only if status is 'pending')
   * @param {string} movementId - Movement ID
   * @param {Object} updateData - Update data
   */
  async updateMovement(movementId, updateData) {
    try {
      const response = await httpClient.put(`/api/warehouse-movements/${movementId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating warehouse movement:', error);
      throw error;
    }
  },

  /**
   * Confirm a pending outbound movement and update inventory
   * @param {string} movementId - Movement ID
   */
  async confirmMovement(movementId) {
    try {
      const response = await httpClient.post(`/api/warehouse-movements/${movementId}/confirm`);
      return response.data;
    } catch (error) {
      console.error('Error confirming warehouse movement:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending warehouse movement
   * @param {string} movementId - Movement ID
   */
  async cancelMovement(movementId) {
    try {
      const response = await httpClient.post(`/api/warehouse-movements/${movementId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling warehouse movement:', error);
      throw error;
    }
  },

  // ==================== Delete Operations ====================

  /**
   * Delete a warehouse movement (admin only, and only if pending or cancelled)
   * @param {string} movementId - Movement ID
   */
  async deleteMovement(movementId) {
    try {
      const response = await httpClient.delete(`/api/warehouse-movements/${movementId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting warehouse movement:', error);
      throw error;
    }
  },

  // ==================== Statistics & Export ====================

  /**
   * Get warehouse movement statistics
   */
  async getStatistics() {
    try {
      const response = await httpClient.get('/api/warehouse-movements/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse statistics:', error);
      throw error;
    }
  },

  /**
   * Export warehouse movements to Excel
   * @param {Object} params - Export parameters
   * @param {Array} params.movement_ids - Optional array of movement IDs
   * @param {string} params.movement_type - 'inbound' | 'outbound'
   * @param {string} params.date_from - Start date filter
   * @param {string} params.date_to - End date filter
   * @param {string} params.template_type - 'inbound' | 'outbound'
   */
  async exportMovements(params) {
    try {
      const response = await httpClient.post('/api/warehouse-movements/export', params, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `warehouse-movements-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error exporting warehouse movements:', error);
      throw error;
    }
  },

  // ==================== Helper Functions ====================

  /**
   * Format movement type for display
   * @param {string} type - Movement type
   */
  getMovementTypeLabel(type) {
    const labels = {
      'inbound': '入库',
      'outbound': '出库',
      'transfer': '调拨'
    };
    return labels[type] || type;
  },

  /**
   * Format status for display
   * @param {string} status - Movement status
   */
  getStatusLabel(status) {
    const labels = {
      'pending': '待确认',
      'completed': '已完成',
      'cancelled': '已取消'
    };
    return labels[status] || status;
  },

  /**
   * Get status color for Material-UI
   * @param {string} status - Movement status
   */
  getStatusColor(status) {
    const colors = {
      'pending': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  },

  /**
   * Format reference type for display
   * @param {string} type - Reference type
   */
  getReferenceTypeLabel(type) {
    const labels = {
      'purchase_order': '采购单',
      'sales_order': '销售单',
      'manual': '手动创建'
    };
    return labels[type] || type;
  }
};

export default warehouseService;